import dayjs from "dayjs";

import type { CreateMessageTemplateInput, MessageTemplate } from "../types";
import {
  DEFAULT_WHATSAPP_TEMPLATE_CATEGORY,
  DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE,
  extractUniqueNamedVariables,
  normalizeTemplateInput,
  validateBodyText,
  validateButtonLabel,
  validateButtonUrl,
  validateHeaderText,
  validateTemplateName,
} from "../validation/messageTemplateValidation";

type WhatsAppTextFormat = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";

type WhatsAppNamedParamExample = {
  param_name: string;
  example: string;
};

type WhatsAppTemplateComponentExample = {
  body_text_named_params?: WhatsAppNamedParamExample[];
  header_text_named_params?: WhatsAppNamedParamExample[];
};

type WhatsAppTemplateComponent = {
  type: "BODY" | "HEADER" | "BUTTONS";
  /**
   * A API aceita `format` para HEADER textual.
   * Para BODY, no fluxo atual, enviamos sem `format` para manter
   * compatibilidade com o payload legado esperado.
   */
  format?: WhatsAppTextFormat;
  text?: string;
  example?: WhatsAppTemplateComponentExample;
  buttons?: {
    type: "URL";
    text: string;
    url?: string;
    phone_number?: string;
  }[];
};

type WhatsAppTemplatePayload = {
  name: string;
  language: string;
  category: "MARKETING";
  components: WhatsAppTemplateComponent[];
  parameter_format?: "named";
};

type RawWhatsAppTemplate = {
  id?: unknown;
  template_id?: unknown;
  name?: unknown;
  language?: unknown;
  category?: unknown;
  status?: unknown;
  created_at?: unknown;
  createdAt?: unknown;
  components?: unknown;
};

const DEFAULT_VARIABLE_EXAMPLE = "Exemplo";

const VARIABLE_EXAMPLE_BY_NAME: Record<string, string> = {
  nome_paciente: "João Silva",
  nome_clinica: "Clínica Veterinária",
  data_consulta: "20/03/2026",
  hora_consulta: "14:30",
  nome_profissional: "Dr. Ana Silva",
  especialidade: "Clínica geral",
  endereco_consulta: "Rua Exemplo, 100",
  link_teleconsulta: "https://meet.exemplo.com/sala",
  motivo_cancelamento: "Indisponibilidade de agenda",
  valor_consulta: "R$ 150,00",
  data_vencimento: "25/03/2026",
  link_pagamento: "https://pagamento.exemplo.com/abc",
  nome_medicamento: "Dipirona 500mg",
  posologia: "1 comprimido a cada 8 horas",
  observacao: "Tomar após as refeições",
  nome_exame: "Hemograma completo",
  data_exame: "22/03/2026",
  orientacao: "Jejum de 8 horas",
  link_resultado: "https://resultados.exemplo.com/abc",
  questionnaire_link: "https://fluxmed.com/questionario/abc123",
};

function toIsoString(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const d = dayjs(value);
    return d.isValid() ? d.toISOString() : null;
  }

  if (typeof value === "string") {
    const d = dayjs(value);
    return d.isValid() ? d.toISOString() : null;
  }

  return null;
}

function buildNamedParamsExample(
  variableNames: string[],
): WhatsAppNamedParamExample[] {
  return variableNames.map((param_name) => ({
    param_name,
    example: VARIABLE_EXAMPLE_BY_NAME[param_name] ?? DEFAULT_VARIABLE_EXAMPLE,
  }));
}

export function buildWhatsAppTemplatePayloadFromUiInput(
  input: CreateMessageTemplateInput | MessageTemplate,
  params?: {
    language?: string;
    category?: WhatsAppTemplatePayload["category"];
  },
): WhatsAppTemplatePayload {
  const language = params?.language ?? DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE;
  const category = params?.category ?? DEFAULT_WHATSAPP_TEMPLATE_CATEGORY;

  if (language !== DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE) {
    throw new Error(
      `Idioma inválido. Use "${DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE}".`,
    );
  }

  if (category !== DEFAULT_WHATSAPP_TEMPLATE_CATEGORY) {
    throw new Error(
      `Categoria inválida. Use "${DEFAULT_WHATSAPP_TEMPLATE_CATEGORY}".`,
    );
  }

  const normalizedInput = normalizeTemplateInput({
    name: input.name,
    header: input.header,
    body: input.preview,
  });

  const nameValidation = validateTemplateName(normalizedInput.name);
  if (!nameValidation.valid) {
    throw new Error(nameValidation.error);
  }

  const headerText = normalizedInput.header;
  const bodyText = normalizedInput.body;

  const headerValidation = validateHeaderText(headerText);
  if (!headerValidation.valid) {
    throw new Error(headerValidation.error);
  }

  const bodyValidation = validateBodyText(bodyText);
  if (!bodyValidation.valid) {
    throw new Error(bodyValidation.error);
  }

  const headerVariables = headerText
    ? extractUniqueNamedVariables(headerText)
    : [];
  const bodyVariables = extractUniqueNamedVariables(bodyText);

  const components: WhatsAppTemplateComponent[] = [];

  if (headerText) {
    const headerComponent: WhatsAppTemplateComponent = {
      type: "HEADER",
      format: "TEXT",
      text: headerText,
      ...(headerVariables.length > 0
        ? {
          example: {
            header_text_named_params:
              buildNamedParamsExample(headerVariables),
          },
        }
        : {}),
    };
    components.push(headerComponent);
  }

  components.push({
    type: "BODY",
    text: bodyText,
    ...(bodyVariables.length > 0
      ? {
        example: {
          body_text_named_params: buildNamedParamsExample(bodyVariables),
        },
      }
      : {}),
  });

  if (input.buttonType && input.buttonText) {
    const buttonText = input.buttonText.trim();
    const buttonValue = (input.buttonValue ?? "").trim();

    const labelValidation = validateButtonLabel(buttonText);
    if (!labelValidation.valid) throw new Error(labelValidation.error);

    if (input.buttonType !== "URL") {
      throw new Error("Valor do botão é obrigatório para URL.");
    }

    const urlValidation = validateButtonUrl(buttonValue);
    if (!urlValidation.valid) throw new Error(urlValidation.error);

    components.push({
      type: "BUTTONS",
      buttons: [
        {
          type: input.buttonType,
          text: buttonText,
          url: buttonValue,
        },
      ],
    });
  }

  const hasNamedParams = headerVariables.length > 0 || bodyVariables.length > 0;

  const payload: WhatsAppTemplatePayload = {
    name: normalizedInput.name,
    language,
    category,
    components,
    ...(hasNamedParams ? { parameter_format: "named" as const } : {}),
  };

  return payload;
}

export function mapRawWhatsAppTemplateToUiTemplate(
  raw: RawWhatsAppTemplate,
): MessageTemplate {
  const id = String(raw.id ?? raw.template_id ?? raw.name ?? "");
  const name = String(raw.name ?? "");
  const status = String(raw.status ?? "");

  const components = Array.isArray(raw.components)
    ? (raw.components as any[])
    : [];
  const headerComponent = components.find((c) => c?.type === "HEADER") as any;
  const bodyComponent = components.find((c) => c?.type === "BODY") as any;
  const buttonComponent = components.find((c) => c?.type === "BUTTONS") as any;
  const firstButton = Array.isArray(buttonComponent?.buttons)
    ? buttonComponent.buttons[0]
    : null;

  const header = String(headerComponent?.text ?? "");
  const preview = String(bodyComponent?.text ?? "");

  const createdAtIso =
    toIsoString(raw.created_at) ??
    toIsoString(raw.createdAt) ??
    dayjs().toISOString();

  return {
    id,
    name,
    header,
    preview,
    status,
    buttonType: firstButton?.type,
    buttonText: String(firstButton?.text ?? ""),
    buttonValue:
      String(firstButton?.url ?? firstButton?.phone_number ?? ""),
    createdAtIso,
  };
}
